import React, { useState, useEffect, useMemo } from "react"
import { Nav, Tab } from "react-bootstrap"
import { useAccessContext } from "../component/guard/AccessGuard"
import { firestore } from "../core"
import { SystemNotificationDocument, UserId, SystemNotificationLaunchPopup, SystemNotificationId } from "popopo-msg"
import {
  useSystemNotificationLaunchPopups,
  useSystemNotificationReadStatuses,
  useUserSystemNotificationCheckStatus,
  useNewSystemNotifications,
  updateUserSystemNotificationLastCheckedAt,
  useUnreadPersonalNotificationCount
} from "popopo-lib-front/firestore-access"
import { LaunchPopup } from "../component/LaunchPopup"
import { sortBy } from "popopo-lib-common"
import { PersonalNotifications } from "../components/PersonalNotifications"
import { SystemNotifications } from "../components/SystemNotifications"

export const Notifications = () => {
  const context = useAccessContext()
  const { user } = context
  const userId = UserId(user.id)

  const launchPopupNotifications = useSystemNotificationLaunchPopups(firestore)
  const launchPopupNotificationIds = useMemo(() => {
    return launchPopupNotifications === "loading" ? [] : launchPopupNotifications.map(n => n.id)
  }, [launchPopupNotifications])
  const launchPopupReadStatuses = useSystemNotificationReadStatuses(firestore, userId, launchPopupNotificationIds)
  
  const systemNotificationCheckStatus = useUserSystemNotificationCheckStatus(firestore, userId)
  const lastSystemNotificationCheckedAt = systemNotificationCheckStatus === "loading" ? null : systemNotificationCheckStatus?.last_checked_at?.seconds
  const hasNewNotifications = useNewSystemNotifications(firestore, lastSystemNotificationCheckedAt == null ? undefined : lastSystemNotificationCheckedAt * 1000)

  const unreadPersonalNotificationCount = useUnreadPersonalNotificationCount(firestore, userId)

  const [launchPopupToShow, setLaunchPopupToShow] = useState<SystemNotificationDocument | null>(null)
  const [dismissedPopups, setDismissedPopups] = useState<SystemNotificationId[]>([])

  useEffect(() => {
    if (launchPopupNotifications === "loading" || launchPopupReadStatuses === "loading") return

    const unreadPopupNotifications = launchPopupNotifications.filter(notification => {
      const readStatus = launchPopupReadStatuses.find(rs => rs.system_notification_id === notification.id)
      return !readStatus?.has_seen_launch_popup && !dismissedPopups.includes(notification.id)
    })

    if (unreadPopupNotifications.length === 0) {
      setLaunchPopupToShow(null)
      return
    }

    const highestNotifications = unreadPopupNotifications.filter(n => n.launch_popup === SystemNotificationLaunchPopup.Highest)
    const lowestNotifications = unreadPopupNotifications.filter(n => n.launch_popup === SystemNotificationLaunchPopup.Lowest)

    let targetNotification: SystemNotificationDocument | null = null

    if (0 < highestNotifications.length) {
      targetNotification = highestNotifications.sort(
        sortBy((n: SystemNotificationDocument) => [-n.display_period.start_at])
      )[0]
    } else if (0 < lowestNotifications.length) {
      targetNotification = lowestNotifications.sort(
        sortBy((n: SystemNotificationDocument) => [-n.display_period.start_at])
      )[0]
    }

    if (targetNotification) {
      setLaunchPopupToShow(targetNotification)
    } else {
      setLaunchPopupToShow(null)
    }
  }, [launchPopupNotifications, launchPopupReadStatuses, dismissedPopups])

  const handleSystemTabClick = async () => {
    try {
      await updateUserSystemNotificationLastCheckedAt(firestore, userId)
    } catch (e) {
      console.error("Failed to update last checked at:", e)
    }
  }

  return (
    <div className="row gy-4 justify-content-center">
      <div className="col-12 col-xl-8">
        <h2 className="mb-4">
          <i className="fas fa-bell me-2"></i>
          お知らせ
        </h2>

        <Tab.Container defaultActiveKey="personal">
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="personal">
                <i className="fas fa-user me-2"></i>
                あなたへのお知らせ
                {unreadPersonalNotificationCount !== "loading" && 0 < unreadPersonalNotificationCount && (
                  <span className="badge bg-danger ms-2">{unreadPersonalNotificationCount}</span>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="system" onClick={handleSystemTabClick}>
                <i className="fas fa-bullhorn me-2"></i>
                運営からのお知らせ
                {hasNewNotifications === true && (
                  <span className="badge bg-danger ms-2">NEW</span>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="personal">
              <PersonalNotifications />
            </Tab.Pane>

            <Tab.Pane eventKey="system">
              <SystemNotifications />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {launchPopupToShow && (
          <LaunchPopup
            notification={launchPopupToShow}
            show={true}
            userId={userId}
            onClose={() => {
              setDismissedPopups(prev => [...prev, launchPopupToShow.id])
              setLaunchPopupToShow(null)
            }}
          />
        )}
      </div>
    </div>
  )
}
